import { Injectable } from '@angular/core';
import { Campaign } from '../../../models/campaign';
import { CampaignHelperService } from '../../../services/campaign-helper.service';
import { ChannelStatus } from 'src/app/models/channel';
import { getChannelSupplierStatusForMedia } from 'src/app/shared/utility/campaign';
import { RulesHelper } from './rules-helper';

@Injectable({
    providedIn: 'root'
})
export class MediaHelperService {
    mediasData: any;

    constructor(private campaignHelperService: CampaignHelperService) { }

    populateMediaList(campaign: Campaign, mediasData: any): { mediasList: any[], rulesArrayValues: any[] } {
        const mediasList = [];
        const rulesArrayValues = [];

        for (let [key, channels] of mediasData) {
            mediasList.push({
                resolution: key,
                idChannel: channels[0].idChannel,
                subMedias: [{
                    title: '',
                    asset: [],
                    mediaChannels: [{ key: '', channelId: '', op: '', value: '' }]
                }]
            });
            rulesArrayValues.push({ resolution: key, rules: [[[]]] });
        }

        const placeholderConsumed = new Set<number>();
        const attach = (index: number, mediaItem: any) => {
            if (placeholderConsumed.has(index)) {
                mediasList[index].subMedias.push(mediaItem);
            } else {
                mediasList[index].subMedias = [mediaItem];
                placeholderConsumed.add(index);
            }
        };

        if (campaign.products?.length > 0) {
            campaign.products.forEach(product => {
                (product.media || []).forEach((mediaItem: any) => {
                    let channelId = mediaItem.mediaChannels?.[0]?.channelId;
                    if ((channelId === '' || channelId == null) && product.channels?.length) {
                        channelId = product.channels[0];
                    }
                    const res = this.getResolutionOfChannelId(channelId, mediasData);
                    const index = mediasList.findIndex(m => m.resolution === res);

                    if (index !== -1) {
                        attach(index, mediaItem);
                    }
                });
            });
        }

        if ((campaign as any).removedProducts?.length > 0) {
            (campaign as any).removedProducts.forEach((product: any) => {
                (product.media || []).forEach((mediaItem: any) => {
                    // mediaChannels may be empty if stripped during channel removal; fall back to product channel
                    let channelId = mediaItem.mediaChannels?.[0]?.channelId;
                    if (!channelId && product.channels?.length) {
                        channelId = product.channels[0];
                    }
                    if (!channelId) return;
                    const res = this.getResolutionOfChannelId(channelId, mediasData);
                    const index = mediasList.findIndex(m => m.resolution === res);

                    if (index !== -1) {
                        attach(index, mediaItem);
                    }
                });
            });
        }

        this.populateRulesArray(mediasList, rulesArrayValues, mediasData);

        return { mediasList, rulesArrayValues };
    }

    private populateRulesArray(mediasList, rulesArrayValues, mediasData) {
        mediasList.forEach((media, ml) => {
            if (!rulesArrayValues[ml].rules) rulesArrayValues[ml].rules = [];

            media.subMedias.forEach((subMedia, sm) => {
                if (!rulesArrayValues[ml].rules[sm]) rulesArrayValues[ml].rules[sm] = [];

                (subMedia.mediaChannels || []).forEach((curr, m) => {
                    const res = this.getResolutionOfChannelId(curr.channelId, mediasData);
                    const allRules = this.getRulesByChannelIdAndResolution(res, curr.channelId, mediasData);

                    const selectedRule = allRules.find(r =>
                        curr.key == r.ruleKey &&
                        r.ruleOperatorEnum == curr.op &&
                        r.value == curr.value
                    );

                    if (!rulesArrayValues[ml].rules[sm][m]) rulesArrayValues[ml].rules[sm][m] = [];

                    if (selectedRule) {
                        rulesArrayValues[ml].rules[sm][m] = selectedRule;
                    }
                });
            });
        });
    }

    addSubMedia(mediasList: any[], rulesArrayValues: any[], pos: number) {
        const subMedias = mediasList[pos].subMedias;
        const newSubMedia = {
            title: '',
            asset: [],
            mediaChannels: [{ key: '', channelId: '', op: '', value: '' }]
        };
        subMedias.push(newSubMedia);

        if (!rulesArrayValues[pos].rules[subMedias.length - 1]) {
            rulesArrayValues[pos].rules[subMedias.length - 1] = [null];
        }
    }

    prepareProductMedia(campaign: Campaign, mediasListToUse: any[], keepPartial: boolean = false) {
        if (!campaign?.products) return;

        campaign.products.forEach(product => {
            product.media = [];
            mediasListToUse.forEach(mediaList => {
                mediaList.subMedias.forEach(subMedia => {
                    if (!this.shouldPersistSubMedia(subMedia, keepPartial)) return;
                    if (!this.belongsToProduct(product, mediaList, subMedia, keepPartial)) return;
                    if (!product.media.includes(subMedia)) {
                        product.media.push(subMedia);
                    }
                });
            });
        });
    }

    /** Complete-only for submission; anything the client has actually typed for a draft save. */
    private shouldPersistSubMedia(subMedia: any, keepPartial: boolean): boolean {
        if (!keepPartial) {
            return subMedia.title !== '' && subMedia.asset.length !== 0;
        }
        return !!subMedia.title?.trim()
            || (subMedia.asset?.length ?? 0) > 0
            || (subMedia.mediaChannels || []).some(mc => mc.channelId !== '' && mc.channelId != null);
    }

    private belongsToProduct(product: any, mediaList: any, subMedia: any,
                            keepPartial: boolean): boolean {
        if (product.channels.includes(mediaList.idChannel)) {
            return true;
        }

        if (!keepPartial) {
            return false;
        }
        return (subMedia.mediaChannels || []).some(
            mc => mc.channelId !== '' && mc.channelId != null
                && product.channels.some((c: any) => c == mc.channelId));
    }

    getCompleteMediasList(mediasList: any[]): any[] {
        const completeMedias = [];
        for (const media of mediasList) {
            if (!media.idChannel) continue;

            const completeSubMedias = (media.subMedias || []).filter(sm => RulesHelper.isSubMediaComplete(sm));

            if (completeSubMedias.length > 0) {
                completeMedias.push({ ...media, subMedias: completeSubMedias });
            }
        }
        return completeMedias;
    }

    getResolutionOfChannelId(channelId: string, mediasData: any) {
        if (!mediasData) return null;
        for (let [key, channels] of mediasData) {
            if (channels.find(c => c.idChannel == channelId)) return key;
        }
        return null;
    }

    getRulesByChannelIdAndResolution(resolution: string, channelId: string, mediasData: any) {
        if (!channelId || !mediasData) return [];
        const rules = mediasData.get(resolution)?.find(c => c.idChannel == channelId);
        return rules?.ruleValues || [];
    }

    getChannelStatus(campaign: Campaign, media: any): ChannelStatus {
        return getChannelSupplierStatusForMedia(campaign, media);
    }
}